import axios, { AxiosError, AxiosResponse } from 'axios'

import { environmentVariables } from '../../utils/environmentVariables'
import {
  ErrorCodes,
  FailedAuthenticationError,
  MfaRequiredError,
  TooManyAttemptsError,
  UserFacingError,
} from '../../utils/errors'
import { redirectToUrl } from '../../utils/location'

type AuthenticationTokens = {
  access_token: string
  refresh_token: string
}

type AuthenticationUrl = {
  redirect_to: string
}

type DirectoryApiError = {
  error?: string
  error_code?: string
}

type VerifiedCredentialsUserEmail = {
  email: string
}

interface TwoStepVerificationCodeApiError extends DirectoryApiError {
  recipient: string
  channel: string
}

type TwoStepVerificationDetails = {
  channel: string
  recipient: string
}

export const directoryApiClient = axios.create({
  baseURL: environmentVariables('AUTH_API_URL'),
  headers: {
    'X-Swile-Platform': 'web',
  },
})

export const isSso = async (email: string): Promise<boolean> => {
  try {
    const response = await directoryApiClient.get('/sso/info', { params: { email } })
    return response.data
  } catch (error) {
    console.error(error)
    return false
  }
}

export const ssoInit = (email: string, recaptchaToken: string): void => {
  const redirectUrl = new URL(`${environmentVariables('AUTH_API_URL')}/sso/init`)
  redirectUrl.searchParams.append('email', email)
  redirectUrl.searchParams.append('redirect_uri', environmentVariables('AUTH_SIGNIN_PAGE_URL'))
  redirectUrl.searchParams.append('force_authentication', 'true')
  redirectUrl.searchParams.append('recaptcha_enterprise_token', recaptchaToken)
  redirectUrl.searchParams.append(
    'recaptcha_enterprise_site_key',
    environmentVariables('RECAPTCHA_DIRECTORY_FRONTEND_SITE_KEY'),
  )
  return redirectToUrl(redirectUrl)
}

export const signInPassword = async (
  username: string,
  password: string,
  recaptchaToken: string,
  authenticationCode?: string,
  secondaryFactor?: string,
): Promise<AuthenticationTokens> => {
  return directoryApiClient
    .post('/oauth/token', {
      client_id: environmentVariables('AUTH_CLIENT_ID'),
      grant_type: 'password',
      username,
      password,
      recaptcha_enterprise_token: recaptchaToken,
      recaptcha_enterprise_site_key: environmentVariables('RECAPTCHA_DIRECTORY_FRONTEND_SITE_KEY'),
      authentication_code: authenticationCode,
      secondary_factor: secondaryFactor,
    })
    .then((response: AxiosResponse<AuthenticationTokens>) => response.data)
    .catch((error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          throw new TooManyAttemptsError()
        }
        handleSignInError(error)
      }
      throw new Error('Error during POST oauth/token { grant_type: "password" }', { cause: error })
    })
}

export const signInAuthorizationCode = async (
  username: string,
  password: string,
  recaptchaToken: string,
  redirectTo: string,
  authenticationCode?: string,
  secondaryFactor?: string,
): Promise<AuthenticationUrl> => {
  return directoryApiClient
    .post('/session', {
      username,
      password,
      recaptcha_enterprise_token: recaptchaToken,
      recaptcha_enterprise_site_key: environmentVariables('RECAPTCHA_DIRECTORY_FRONTEND_SITE_KEY'),
      redirect_to: redirectTo,
      remember_me: '1',
      authentication_code: authenticationCode,
      secondary_factor: secondaryFactor,
    })
    .then((response: AxiosResponse<AuthenticationUrl>) => response.data)
    .catch((error) => {
      if (axios.isAxiosError(error)) {
        handleSignInError(error)
      }
      throw new Error('Error during POST session { grant_type: "authorization_code" }', { cause: error })
    })
}

const handleSignInError = (error: AxiosError<DirectoryApiError>): void => {
  if (error.response?.data.error === ErrorCodes.INVALID_GRANT) {
    throw new FailedAuthenticationError()
  }
  if (error.response?.data.error_code === ErrorCodes.RECAPTCHA_INVALID_TOKEN) {
    throw new UserFacingError('global.error_title', 'sign_in_page.form.error.recaptcha')
  }
  if (error.response?.data.error === ErrorCodes.MISSING_AUTHENTICATION_CODE) {
    const { recipient, channel } = error.response.data as TwoStepVerificationCodeApiError
    throw new MfaRequiredError(channel, recipient)
  }
}

export const exchangeToken = async (refreshToken: string): Promise<AuthenticationTokens> => {
  return directoryApiClient
    .post('/oauth/token', {
      client_id: environmentVariables('AUTH_CLIENT_ID'),
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    })
    .then((response: AxiosResponse<AuthenticationTokens>) => response.data)
    .catch((error) => {
      throw new Error('Error during POST oauth/token { grant_type: "refresh_token" }', { cause: error })
    })
}

export const getVerifiedCredentialsUserEmail = async (): Promise<VerifiedCredentialsUserEmail> => {
  const { data } = await directoryApiClient.get('/oauth/accounts/email')
  return data
}

export const updateCurrentlyVerifiedUserEmail = async (
  email: string,
  twoStepVerificationCode: string,
): Promise<VerifiedCredentialsUserEmail> => {
  return directoryApiClient
    .put(
      '/oauth/accounts/email',
      { email },
      { headers: { 'X-Swile-Two-Step-Verification-Code': twoStepVerificationCode } },
    )
    .then((response: AxiosResponse) => response.data)
    .catch((error) => {
      if (error.response.status === 422) {
        throw new UserFacingError('global.error_title', 'change_email.update_email_error')
      }
      if (
        error.response.status === 403 &&
        error.response.data.error.message === ErrorCodes.INVALID_TWO_STEP_VERIFICATION_CODE
      ) {
        const { recipient, channel } = error.response.data as TwoStepVerificationCodeApiError
        throw new MfaRequiredError(channel, recipient)
      }
      throw error
    })
}

export const postTwoStepVerificationCodeToCurrentlyVerifiedUser = async (): Promise<TwoStepVerificationDetails> => {
  return directoryApiClient
    .post('/two_step_verification/code', { channel: 'sms', operation: 'loginemailupdate' })
    .then((response: AxiosResponse<TwoStepVerificationDetails>) => response.data)
    .catch((error) => {
      if (error.response.status === 401) {
        throw new UserFacingError('global.error_title', 'change_email.error_expired_session_error')
      }
      throw error
    })
}
